import { cloneDeep } from "lodash";
import { totalFileSizeQuotient } from "../constants/FileUploadConstant";

export const formatBytes = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

export const downloadFile = (fileOrBlob, fileName = "download") => {
  const url = window.URL.createObjectURL(new Blob([fileOrBlob]));
  const a = document.createElement("a");
  a.href = url;
  a.download = fileOrBlob?.name ? fileOrBlob?.name : fileName; // Set the desired file name
  a.style.display = "none";

  document.body.appendChild(a);
  a.click();

  // Clean up
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

export const orderBySequence = (arr) => {
  return arr.sort(function (a, b) {
    return a.Sequence - b.Sequence;
  });
};

export const onFileUpload = (
  e,
  FileDetails,
  fileUploadSection,
  seq,
  groupTaskDetails,
  setFileGroups,
  orderBySequence,
) => {
  // groupTaskDetails is now a file group object with FileMetaDataList
  const temp = cloneDeep(groupTaskDetails?.FileMetaDataList || []);
  let found = false;
  temp.forEach((obj, i) => {
    if (
      String(FileDetails.FileGroup) === String(obj?.FileGroup) &&
      String(FileDetails.Version) === String(obj?.Version)
    ) {
      found = true;
      obj["File_Name"] = e?.files?.[0]?.name;
      if (e?.files?.[0]?.name !== "") {
        obj["Info"] = {
          fileInfo: e?.files?.[0],
          uploadType: "",
          sequence: seq,
          version: FileDetails?.Version,
          id: i,
          Full_Name:
            FileDetails?.Info === undefined ? FileDetails?.Full_Name : FileDetails?.Info.Full_Name,
          Email: FileDetails?.Info === undefined ? FileDetails?.Email : FileDetails?.Info.Email,
        };
        obj["AzureFile"] =
          FileDetails?.file !== undefined ? FileDetails?.file : FileDetails?.AzureFile;
        obj["Timestamp"] = FileDetails?.Timestamp;
      } else {
        obj["Info"] = "";
        obj["AzureFile"] = "";
      }
    }
  });
  // If not found, add as new version for the group
  if (!found && e?.files?.[0]?.name) {
    temp.unshift({
      ...FileDetails,
      FileGroup: String(FileDetails.FileGroup),
      File_Name: e?.files?.[0]?.name,
      Info: {
        fileInfo: e?.files?.[0],
        uploadType: "",
        sequence: seq,
        version: FileDetails?.Version,
        id: temp.length,
        Full_Name:
          FileDetails?.Info === undefined ? FileDetails?.Full_Name : FileDetails?.Info.Full_Name,
        Email: FileDetails?.Info === undefined ? FileDetails?.Email : FileDetails?.Info.Email,
      },
      AzureFile: FileDetails?.file !== undefined ? FileDetails?.file : FileDetails?.AzureFile,
      Timestamp: FileDetails?.Timestamp,
    });
  }

  setFileGroups((prevGroups) => {
    // Find and update the correct group in the array
    return prevGroups.map((group) =>
      String(group.FileGroup) === String(groupTaskDetails.FileGroup)
        ? { ...group, FileMetaDataList: temp }
        : group,
    );
  });
};

export const onFileClear = (
  fileUploadSection,
  fileListToRemove,
  groupTaskDetails,
  setFileGroups,
) => {
  // groupTaskDetails is a file group object with FileMetaDataList
  const newFileList = (groupTaskDetails?.FileMetaDataList || []).filter(
    (file) => !fileListToRemove.includes(file.FileID),
  );
  setFileGroups((prevGroups) =>
    prevGroups.map((group) =>
      group.FileGroup === groupTaskDetails.FileGroup
        ? { ...group, FileMetaDataList: newFileList }
        : group,
    ),
  );
};

export const addNewFiles = (isNew, taskDetails, setTaskDetails, documentSequence) => {
  if (isNew) {
    return [
      {
        FileGroup: 1,
        isNew: true,
        File_Name: "",
        Sequence: 1,
        Version: 1,
        Email: "",
        FileID: "",
        File_Path: "",
        Full_Name: "",
        JobID: "",
        JobName: "",
        ReadOnlyURL: "",
        Timestamp: "",
        TypeOfFile: "",
        Action: "Add",
      },
    ];
  }
  const clonedTasks = cloneDeep(taskDetails?.file);

  clonedTasks.push({
    FileGroup: documentSequence,
    isNew: true,
    File_Name: "",
    Sequence: documentSequence,
    Version: 1,
    Email: "",
    FileID: "",
    File_Path: "",
    Full_Name: "",
    JobID: "",
    JobName: "",
    ReadOnlyURL: "",
    Timestamp: "",
    TypeOfFile: "",
    Action: "Add",
  });

  // setDocumentSequence is now managed in the calling component
  setTaskDetails((prev) => ({ ...prev, file: clonedTasks }));
};

export const getFilesListObj = async (
  fileGroups,
  TaskDetailsData,
  folderStructure,
  toast,
  uploadtoAzureStrContainer,
  AzureContainerSuccess,
  AzureContainerFail,
) => {
  let newFileList = [];
  for (const group of fileGroups) {
    for (const obj of group.FileMetaDataList) {
      if ("AzureFile" in obj) {
        if (obj.Info) {
          const fileSize = Math.round(obj?.Info?.fileInfo?.size / totalFileSizeQuotient);
          const temp = {
            FileGroup: obj.FileGroup,
            Version: obj.Info.version,
            Size: fileSize === 0 ? "1" : fileSize,
            Sequence: obj?.Sequence,
            Filename: obj.File_Name,
            Full_Name: obj?.Info?.Full_Name,
            Email: obj?.Info?.Email,
            TypeOfFile: obj?.File_Name?.split(".")?.pop(),
            JobID: TaskDetailsData.ArtworkAgilityTasks[0].DesignJobDetails[0].Design_Job_ID,
            // JobName logic may need to be updated for fileGroups context
            JobName: obj.JobName || "",
            Action: "add",
          };
          newFileList.push(temp);
          let azureRes = await uploadtoAzureStrContainer(
            obj?.file === undefined ? obj.AzureFile : obj.file,
            `${folderStructure}/${obj.FileGroup}/${obj.Info.version}/`,
          );
          if (azureRes) {
            if (toast.current) {
              toast.current.show(AzureContainerSuccess);
            }
          } else {
            if (toast.current) {
              toast.current.show(AzureContainerFail);
            }
          }
        }
      }
    }
  }
  return newFileList;
};

/**
 * Utility function to handle file clear operations
 * Supports both array (delete entire group) and single file (delete by version) modes
 */
export const onFileClearHandler = (
  fileListToRemove,
  fileGroups,
  setFileGroups,
  setLoader,
  options = {},
) => {
  const { deleteFileGroupAPI, ProjectID, TaskID, Etag } = options;

  const headers = {
    key: "If-Match",
    value: Etag,
  };

  // If fileListToRemove is an array with more than one file, remove the entire group
  if (Array.isArray(fileListToRemove) && fileListToRemove.length) {
    const allItemsHaveInfo = fileListToRemove.every((item) => item.hasOwnProperty("Info"));
    setLoader(true);

    const fileGroupToRemove =
      fileListToRemove[0]?.FileGroup != null ? Number(fileListToRemove[0]?.FileGroup) : undefined;
    const updatedGroups = fileGroups.filter(
      (group) => Number(group.FileGroup) !== Number(fileGroupToRemove),
    );

    // If not all items have Info property and deleteFileGroupAPI is available, call it
    if (!allItemsHaveInfo && deleteFileGroupAPI) {
      return {
        updatedGroups,
        apiCall: deleteFileGroupAPI(ProjectID, TaskID, fileGroupToRemove, headers),
      };
    }

    setFileGroups(updatedGroups);
    setLoader(false);
    return { updatedGroups };
  } else {
    // Remove by version
    const fileGroupToRemove =
      fileListToRemove?.FileGroup != null ? Number(fileListToRemove?.FileGroup) : undefined;
    const versionToRemove = fileListToRemove?.Version;

    const updatedGroups = fileGroups
      .map((group) => {
        if (Number(group.FileGroup) === fileGroupToRemove) {
          const updatedFileMetaDataList = group.FileMetaDataList.filter(
            (file) => file.Version !== versionToRemove,
          );
          if (updatedFileMetaDataList.length === 0) {
            return null;
          }
          return {
            ...group,
            FileMetaDataList: updatedFileMetaDataList,
          };
        }
        return group;
      })
      .filter(Boolean);

    setFileGroups(updatedGroups);
    return { updatedGroups };
  }
};

/**
 * Utility function to add new file group
 * Calculates next FileGroup number and creates new file metadata structure
 */
export const onAddNewFilesHandler = (fileGroups, setFileGroups, setDocumentSequence) => {
  setFileGroups((prevGroups) => {
    let lastFileGroup = 0;
    if (prevGroups && prevGroups.length > 0) {
      lastFileGroup = Math.max(...prevGroups.map((g) => Number(g.FileGroup) || 0));
    }
    const nextSequence = lastFileGroup + 1;

    if (setDocumentSequence) {
      setDocumentSequence(nextSequence);
    }

    const newGroup = {
      FileGroup: nextSequence,
      Sequence: nextSequence,
      FileMetaDataList: [
        {
          isNew: true,
          FileGroup: nextSequence,
          Sequence: nextSequence,
          File_Name: "",
          Version: 1,
          Email: "",
          FileID: "",
          File_Path: "",
          Full_Name: "",
          JobID: "",
          JobName: "",
          ReadOnlyURL: "",
          Timestamp: "",
          TypeOfFile: "",
        },
      ],
    };

    return [...prevGroups, newGroup];
  });
};

/**
 * Utility function to build submit navigation path
 * Handles conditional routing based on parameters
 */
export const onSubmitNavigationPath = (page1, page2, pageType, TaskDetailsData) => {
  if (page2 && page2 === "projectPlan") {
    return `/${page1}/${page2}/${TaskDetailsData?.ArtworkAgilityPage?.AWM_Project_ID}`;
  } else if (pageType) {
    return `/${pageType}`;
  }
  return null;
};

/**
 * Utility function to handle form submission
 * Manages loader state, calls submit API, and handles navigation
 */
export const onSubmitHandler = async (
  getFilesListObj,
  getFormDataFunction,
  submitAPIFunction,
  navigationPath,
  setLoader,
  navigate,
) => {
  setLoader(true);
  try {
    const submitTaskFiles = await getFilesListObj();
    const { formData, id, headers } = getFormDataFunction(submitTaskFiles);
    await submitAPIFunction(formData, id, headers);
    setLoader(false);

    if (navigationPath) {
      navigate(navigationPath);
    }

    return { success: true };
  } catch (error) {
    setLoader(false);
    console.error("Error during submission:", error);
    throw error;
  }
};
